import { ExternalIntegrationTypes } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Button, Stack, TextField } from "@mui/material";
import { useCreateWorkplaceExternalIntegration } from "@src/appV2/ExternalIntegrations/api/useCreateWorkplaceExternalIntegration";
import { type WorkplaceExternalIntegration } from "@src/appV2/ExternalIntegrations/api/useGetWorkplaceExternalIntegrations";
import { useUpdateWorkplaceExternalIntegration } from "@src/appV2/ExternalIntegrations/api/useUpdateWorkplaceExternalIntegration";
import { useToast } from "@src/appV2/lib";
import { Text } from "@src/appV2/redesign/components/Text";
import { useEffect, useRef, useState } from "react";

function getUkgBusinessUnitName(integration?: WorkplaceExternalIntegration): string | undefined {
  const ukg = integration?.ukg;
  return isDefined(ukg) && "ukgBusinessUnitName" in ukg ? ukg.ukgBusinessUnitName : undefined;
}

interface UkgBusinessUnitNameFieldProps {
  workplaceId: string;
  externalIntegration?: WorkplaceExternalIntegration;
  parentExternalIntegration?: WorkplaceExternalIntegration;
}

export function UkgBusinessUnitNameField(props: UkgBusinessUnitNameFieldProps) {
  const { workplaceId, externalIntegration, parentExternalIntegration } = props;

  const { showSuccessToast, showErrorToast } = useToast();
  const { mutateAsync: createExternalIntegration, isLoading: isCreating } =
    useCreateWorkplaceExternalIntegration();
  const { mutateAsync: updateExternalIntegration, isLoading: isUpdating } =
    useUpdateWorkplaceExternalIntegration();

  const existingBusinessUnitName = getUkgBusinessUnitName(externalIntegration);
  const [businessUnitName, setBusinessUnitName] = useState(existingBusinessUnitName ?? "");
  const [savedName, setSavedName] = useState(existingBusinessUnitName ?? "");
  const createdIntegrationIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const value = existingBusinessUnitName ?? "";
    setBusinessUnitName(value);
    setSavedName(value);
  }, [existingBusinessUnitName]);

  const parentHasUkg = parentExternalIntegration?.type === ExternalIntegrationTypes.UKG;
  const hasChanges = businessUnitName !== savedName;
  const isSaving = isCreating || isUpdating;
  const integrationId = externalIntegration?.id ?? createdIntegrationIdRef.current;

  const handleSave = async () => {
    const trimmedName = businessUnitName.trim();
    if (!trimmedName) {
      return;
    }

    try {
      if (isDefined(integrationId)) {
        await updateExternalIntegration({
          workplaceId,
          externalIntegrationId: integrationId,
          attributes: {
            ukg: { ukgBusinessUnitName: trimmedName },
          },
        });
      } else {
        const result = await createExternalIntegration({
          workplaceId,
          attributes: {
            type: ExternalIntegrationTypes.UKG,
            isEnabled: true,
            ukg: { ukgBusinessUnitName: trimmedName },
          },
        });
        createdIntegrationIdRef.current = result.data.id;
      }

      setSavedName(trimmedName);
      showSuccessToast("UKG Business Unit Name saved successfully");
    } catch {
      showErrorToast("Failed to save UKG Business Unit Name");
    }
  };

  if (!parentHasUkg) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Text variant="body1" sx={{ minWidth: 130 }}>
        Business Unit Name
      </Text>
      <TextField
        value={businessUnitName}
        size="small"
        placeholder="Enter business unit name"
        disabled={isSaving}
        sx={{ width: 280 }}
        onChange={(event) => {
          setBusinessUnitName(event.target.value);
        }}
      />
      <Button
        variant="text"
        size="small"
        aria-label="Save business unit name"
        disabled={isSaving || !businessUnitName.trim() || !hasChanges}
        onClick={() => {
          void handleSave();
        }}
      >
        {isSaving ? "Saving..." : "Save"}
      </Button>
      {isDefined(existingBusinessUnitName) && !(externalIntegration?.isEnabled ?? false) && (
        <Text variant="body2" color="warning.main">
          Disabled
        </Text>
      )}
    </Stack>
  );
}
